/**
 * The selected piece's inspector (design §4): replaces the results rail
 * while something is selected. The editable half of a part, plus the grain
 * row — the piece of the redesign that finally makes `directionVec` real.
 * No approval fields, no göz onayı: warnings are ambient, never gates.
 */
import { Input } from '@app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select'
import type { BomLineOption, SayaMaterialDto } from '../api'
import { HANDEDNESS_VALUES, ORIENTATION_VALUES } from '../api'
import {
  bomLineKey,
  bomLineLabel,
  bomRefFromKey,
  bomRefKey,
  formatDesi,
  HANDEDNESS_LABELS,
  mm2ToDesi,
  NO_BOM_VALUE,
  ORIENTATION_LABELS,
  type PartFormValues,
} from '../format'
import { CountInput } from '../piece-rail'
import { rotateUnitVec } from './pose'
import type { StagePiece } from './stage'

export interface InspectorProps {
  piece: StagePiece
  values: PartFormValues
  materials: SayaMaterialDto[]
  bomLines: BomLineOption[]
  onChange: (patch: Partial<PartFormValues>) => void
  /** Parent computes principalAxisDeg → unit vector → onChange({ directionVec }). */
  onSuggestGrain: () => void
  /** Parent rotates the POSE so the grain vector points up (+y). */
  onAlignGrain: () => void
  onClose: () => void
}

/**
 * The angle the operator SEES: the local grain vector rotated by the pose —
 * so "Yönü hizala" visibly lands the readout on 90°, matching the arrow.
 */
function grainAngleLabel(vec: [number, number] | null, rotDeg: number): string {
  if (!vec) return '—'
  const world = rotateUnitVec(vec, rotDeg)
  const deg = Math.round((Math.atan2(world[1], world[0]) * 180) / Math.PI)
  return `${deg}°`
}

export function Inspector({
  piece,
  values,
  materials,
  bomLines,
  onChange,
  onSuggestGrain,
  onAlignGrain,
  onClose,
}: InspectorProps) {
  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-lg font-semibold">
          {formatDesi(mm2ToDesi(piece.netMm2))} <span className="text-micro text-ink-muted">desi</span>
        </span>
        <button type="button" onClick={onClose} className="text-micro text-ink-muted hover:text-ink">
          ‹ Sonuçlara dön
        </button>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium text-ink-muted">Parça adı</span>
        <Input
          value={values.name}
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium text-ink-muted">Malzeme</span>
        <Select
          value={values.materialId == null ? '' : String(values.materialId)}
          onValueChange={(value) => onChange({ materialId: Number(value) })}
        >
          <SelectTrigger aria-label="Malzeme">
            <SelectValue placeholder="Malzeme seçin" />
          </SelectTrigger>
          <SelectContent>
            {materials.map((material) => (
              <SelectItem key={material.id} value={String(material.id)}>
                {material.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      <div className="flex flex-col gap-1">
        <label
          htmlFor={`inspector-qty-${piece.key}`}
          className="text-micro font-medium text-ink-muted"
        >
          Adet / çift
        </label>
        <CountInput
          id={`inspector-qty-${piece.key}`}
          label="Adet / çift"
          value={values.qtyPerPair}
          onChange={(qtyPerPair) => onChange({ qtyPerPair })}
          disabled={false}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-micro font-medium text-ink-muted">El kuralı</span>
          <Select
            value={values.handedness}
            onValueChange={(value) => onChange({ handedness: value as PartFormValues['handedness'] })}
          >
            <SelectTrigger aria-label="El kuralı">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HANDEDNESS_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {HANDEDNESS_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-micro font-medium text-ink-muted">Yerleşim</span>
          <Select
            value={values.orientation}
            onValueChange={(value) =>
              onChange({ orientation: value as PartFormValues['orientation'] })
            }
          >
            <SelectTrigger aria-label="Yerleşim">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORIENTATION_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {ORIENTATION_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-micro font-medium text-ink-muted">Yön (hav / desen)</span>
        <div className="flex items-center gap-2">
          <span className="rounded-xs border border-hairline px-2 py-1 font-mono text-micro">
            {grainAngleLabel(values.directionVec, piece.pose?.rotDeg ?? 0)}
          </span>
          <button
            type="button"
            onClick={onSuggestGrain}
            className="rounded-xs border border-hairline px-2 py-1 text-micro text-ink-muted transition-colors hover:text-ink"
          >
            Yön öner
          </button>
          <button
            type="button"
            onClick={onAlignGrain}
            disabled={values.directionVec == null}
            className="rounded-xs border border-hairline px-2 py-1 text-micro text-ink-muted transition-colors hover:text-ink disabled:opacity-40"
          >
            ↑ Yönü hizala
          </button>
        </div>
        <span className="text-micro text-ink-muted">
          Ok parça ile birlikte döner; hizala parçayı oku yukarı bakacak şekilde çevirir.
        </span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium text-ink-muted">BOM satırı</span>
        <Select
          value={values.bomAnswered ? bomRefKey(values.bomRef) : ''}
          onValueChange={(value) =>
            onChange(
              value === NO_BOM_VALUE
                ? { bomRef: null, bomAnswered: true }
                : { bomRef: bomRefFromKey(value, bomLines), bomAnswered: true },
            )
          }
        >
          <SelectTrigger aria-label="BOM satırı">
            <SelectValue placeholder="BOM satırı seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_BOM_VALUE}>BOM&apos;da yok</SelectItem>
            {bomLines.map((line) => (
              <SelectItem key={bomLineKey(line)} value={bomLineKey(line)}>
                {bomLineLabel(line)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
    </div>
  )
}
